import React, { useCallback, useState } from 'react'
import { ControllerRenderProps } from 'react-hook-form'
import { gql, useMutation } from '@apollo/client'
import { FormControl, FormItem, FormMessage } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { ImageIcon, Cross2Icon, ReloadIcon } from '@radix-ui/react-icons'

const UPLOAD_IMAGE = gql`
  mutation uploadImage($file: Upload!, $name: String) {
    uploadImage(file: $file, name: $name)
  }
`

interface ImageUploadV2Props {
  field: ControllerRenderProps<any, any>
  imageName?: string
  existingImageUrl?: string
}

export function ImageUploadV2({
  field,
  imageName = 'account-manager-image',
  existingImageUrl,
}: ImageUploadV2Props) {
  const [uploadImage] = useMutation(UPLOAD_IMAGE)
  const [isUploading, setIsUploading] = useState(false)
  const [imagePreview, setImagePreview] = useState<string | null>(null)

  const handleImageUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      setIsUploading(true)
      try {
        // Create preview URL
        const previewUrl = URL.createObjectURL(file)
        setImagePreview(previewUrl)

        const { data } = await uploadImage({
          variables: {
            file,
            name: imageName,
          },
        })

        if (data?.uploadImage) {
          // The mutation returns imageId
          field.onChange(data.uploadImage)
        }
      } catch (error) {
        console.error('Upload error:', error)
        setImagePreview(null)
      } finally {
        setIsUploading(false)
      }
    },
    [field.onChange, uploadImage, imageName]
  )

  const handleRemoveImage = () => {
    field.onChange(null)
    setImagePreview(null)
    // Clean up the preview URL
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview)
    }
  }

  const [isDragging, setIsDragging] = useState(false)

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      const file = e.dataTransfer.files?.[0]
      if (!file || !file.type.startsWith('image/')) return

      setIsUploading(true)
      try {
        // Create preview URL
        const previewUrl = URL.createObjectURL(file)
        setImagePreview(previewUrl)

        const { data } = await uploadImage({
          variables: {
            file,
            name: imageName,
          },
        })

        if (data?.uploadImage) {
          // The mutation returns imageId
          field.onChange(data.uploadImage)
        }
      } catch (error) {
        console.error('Upload error:', error)
        setImagePreview(null)
      } finally {
        setIsUploading(false)
      }
    },
    [field.onChange, uploadImage, imageName]
  )

  const hasImage = field.value || imagePreview || existingImageUrl

  return (
    <div
      className={`col-span-2 w-full ${
        !hasImage
          ? 'p-4 border-2 border-dashed rounded-lg transition-colors ' +
            (isDragging ? 'border-primary bg-primary/5' : 'border-gray-200')
          : ''
      }`}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Image Preview */}
      {hasImage && (
        <div className="relative group border rounded-md overflow-hidden inline-block">
          {imagePreview ? (
            <img
              src={imagePreview}
              alt="Profile preview"
              className="w-24 h-24 object-cover"
            />
          ) : existingImageUrl ? (
            <img
              src={existingImageUrl}
              alt="Profile image"
              className="w-24 h-24 object-cover"
            />
          ) : (
            <div className="w-24 h-24 bg-gray-100 flex items-center justify-center">
              <ImageIcon className="h-8 w-8 text-gray-400" />
              <span className="sr-only">Image ID: {field.value}</span>
            </div>
          )}
          <div className="absolute top-1 right-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 bg-white/80 hover:bg-white/90"
              onClick={handleRemoveImage}
            >
              <Cross2Icon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Upload Button - Only shown if no image */}
      {!hasImage && (
        <div className="flex items-center justify-between gap-4 w-full">
          <p className="text-sm text-gray-500">Drag and drop an image here</p>
          <input
            type="file"
            id={`image-upload-${field.name}`}
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              document.getElementById(`image-upload-${field.name}`)?.click()
            }
            disabled={isUploading}
          >
            {isUploading ? (
              <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ImageIcon className="mr-2 h-4 w-4" />
            )}
            {isUploading ? 'Uploading...' : 'Upload Image'}
          </Button>
        </div>
      )}
    </div>
  )
}
